import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import merge from 'lodash/merge'
import { appListenerMiddleware } from 'reduxTyped'
import { DeepPartial } from 'utility-types'
import { l } from '@manychat/manyui'

import { AiAlwaysOnAutomationsState, LoaderStatus } from 'apps/aiAlwaysOnAutomations/store/types'
import { AiCommunicationsActions } from 'apps/aiCommunications/store'
import { billing, UpgradeModalVersion, UpgradeSource } from 'common/billing'
import { upgrade } from 'common/billing/actions/upgradeActions'
import { cancelUpgradeRequest } from 'common/billing/actions/upgradeRequestActions'
import { AddOnSlug } from 'common/billing/interfaces/addOnTypes'
import { alert } from 'common/core'
import { Ability } from 'common/core/constants/Ability'
import { IThunkAction } from 'common/core/interfaces/actions'
import { getHasAbility } from 'common/core/selectors/abilitiesSelectors'
import { AiAutomationApi } from 'shared/api/requests/aiAutomation'
import { AiAutomationConfig, BusinessContextChunk } from 'shared/api/requests/aiAutomation/schemas'
import { createAsyncAction } from 'shared/lib/redux'

const NAMESPACE = 'aiAlwaysOnAutomations'

export const AiAlwaysOnAutomationsInitialState: AiAlwaysOnAutomationsState = {
  config: {
    business_context: [],
    scenarios: {
      faq: {
        published: false,
      },
    },
  },
  selectedKnowledgeChunkIndex: null,
  loaderStatus: LoaderStatus.NOT_LOADED,
}

const { reducer, actions } = createSlice({
  initialState: AiAlwaysOnAutomationsInitialState,
  name: NAMESPACE,
  reducers: {
    dataUpdated: (state, action: PayloadAction<AiAutomationConfig>) => {
      state.config = action.payload
    },
    selectedKnowledgeChunkIndexUpdated: (state, action: PayloadAction<number | null>) => {
      state.selectedKnowledgeChunkIndex = action.payload
    },
    loaderStatusUpdated: (state, action: PayloadAction<LoaderStatus>) => {
      state.loaderStatus = action.payload
    },
  },
})

const showPaywallAndSetUpPurchaseListener = (): IThunkAction => (dispatch, getState) => {
  dispatch(AiCommunicationsActions.setTriggeredBySetLive(true))
  billing.requestUpgrade({
    addOns: [AddOnSlug.AI],
    source: UpgradeSource.AI_BULK_FAQ,
    customProps: {
      [UpgradeModalVersion.UPGRADE_AI]: {
        indicativeMessage: l.translate(
          'Your AI automation will go live when you start a Pro plan with AI!',
        ),
      },
      [UpgradeModalVersion.UPGRADE_AI_FROM_TRIAL]: {
        indicativeMessage: l.translate(
          'Your AI automation will go live when you start a Pro plan with AI!',
        ),
      },
    },
  })

  const endActions = [upgrade.fulfilled.type, cancelUpgradeRequest.type]
  const publishFlowUnsubscribe = appListenerMiddleware.startListening({
    predicate: (action) => endActions.includes(action.type),
    effect: () => {
      if (getHasAbility(getState(), Ability.AI_KEYWORD)) {
        dispatch(AiAlwaysOnAutomationsActions.updateFaqScenarioStatusFx(true))
      }
      publishFlowUnsubscribe()
    },
  })
}

export const AiAlwaysOnAutomationsReducer = reducer

const getState = (state: RootState) => state.aiAlwaysOnAutomations

export const AiAlwaysOnAutomationsSelectors = {
  getState,
  getBusinessContext: (state: RootState) => getState(state).config.business_context,
  getSelectedKnowledgeChunkIndex: (state: RootState) => getState(state).selectedKnowledgeChunkIndex,
  getSelectedKnowledgeChunk: (state: RootState) => {
    const index = getState(state).selectedKnowledgeChunkIndex
    if (index === null) {
      return undefined
    }
    return getState(state).config.business_context[index]
  },
  getFaqScenarioStatus: (state: RootState) => getState(state).config.scenarios.faq.published,
  getConfig: (state: RootState) => getState(state).config,
  getLoaderStatus: (state: RootState) => getState(state).loaderStatus,
}

const getConfigFx = createAsyncAction(`${NAMESPACE}/getConfig`, async (_, { dispatch }) => {
  dispatch(actions.loaderStatusUpdated(LoaderStatus.DASHBOARD_LOADING))

  const { data } = await AiAutomationApi.getConfig()
  dispatch(actions.dataUpdated(data.config))
  dispatch(actions.loaderStatusUpdated(LoaderStatus.LOADED))
})

const updateConfigFx = createAsyncAction(
  `${NAMESPACE}/updateConfig`,
  async (changes: DeepPartial<AiAutomationConfig>, { dispatch, getState }) => {
    const config = AiAlwaysOnAutomationsSelectors.getConfig(getState())
    const newScenarios = merge({}, config.scenarios, changes.scenarios)
    const newConfig: AiAutomationConfig = {
      scenarios: newScenarios,
      business_context: (changes.business_context ||
        config.business_context) as BusinessContextChunk[],
    }

    if (newConfig.business_context.length === 0) {
      newConfig.scenarios.faq.published = false
    }

    await AiAutomationApi.updateConfig({
      body: { config: newConfig },
    })

    dispatch(actions.dataUpdated(newConfig))
    dispatch(actions.loaderStatusUpdated(LoaderStatus.LOADED))
  },
)

const removeChunkFx = createAsyncAction(
  `${NAMESPACE}/removeChunkFx`,
  async (removedChunkIndex: number, { dispatch, getState }) => {
    const config = AiAlwaysOnAutomationsSelectors.getConfig(getState())
    const updatedContext = config.business_context.filter((_, index) => index !== removedChunkIndex)

    const newConfig = {
      ...config,
      business_context: updatedContext,
    }

    await dispatch(updateConfigFx(newConfig))
  },
)

const editKnowledgeChunkFx = createAsyncAction(
  `${NAMESPACE}/editKnowledgeChunkFx`,
  async (data: BusinessContextChunk, { dispatch, getState }) => {
    const config = AiAlwaysOnAutomationsSelectors.getConfig(getState())
    const index = AiAlwaysOnAutomationsSelectors.getSelectedKnowledgeChunkIndex(
      getState(),
    ) as number

    const newBusinessContext = [...config.business_context]
    newBusinessContext[index] = data

    dispatch(actions.loaderStatusUpdated(LoaderStatus.KNOWLEDGE_EDITOR_LOADING))
    await dispatch(updateConfigFx({ business_context: newBusinessContext }))
    dispatch(actions.selectedKnowledgeChunkIndexUpdated(null))
    alert('Chunk was been edited', 'success')
  },
  {
    condition: (_, { getState }) =>
      Boolean(AiAlwaysOnAutomationsSelectors.getSelectedKnowledgeChunk(getState())),
  },
)

const addKnowledgeChunkFx = createAsyncAction(
  `${NAMESPACE}/addKnowledgeChunkFx`,
  async (data: BusinessContextChunk, { dispatch, getState }) => {
    const config = AiAlwaysOnAutomationsSelectors.getConfig(getState())

    dispatch(actions.loaderStatusUpdated(LoaderStatus.KNOWLEDGE_EDITOR_LOADING))
    await dispatch(updateConfigFx({ business_context: [data, ...config.business_context] }))
    alert('Chunk was been added', 'success')
  },
)

const updateFaqScenarioStatusFx = createAsyncAction(
  `${NAMESPACE}/updateFaqScenarioStatusFx`,
  async (status: boolean, { dispatch, getState }) => {
    const userHasAIAbility = getHasAbility(getState(), Ability.AI_KEYWORD)
    if (!userHasAIAbility && status) {
      dispatch(showPaywallAndSetUpPurchaseListener())
      return
    }

    dispatch(actions.loaderStatusUpdated(LoaderStatus.FAQ_LIVE_LOADING))
    await dispatch(
      updateConfigFx({
        scenarios: {
          faq: {
            published: status,
          },
        },
      }),
    )
  },
)

export const AiAlwaysOnAutomationsActions = {
  removeChunkFx,
  selectedKnowledgeChunkIndexUpdated: actions.selectedKnowledgeChunkIndexUpdated,
  editKnowledgeChunkFx,
  addKnowledgeChunkFx,
  updateFaqScenarioStatusFx,
  getConfigFx,
}
